<template>
  <NodeViewWrapper class="planning-image-node my-2">
    <figure
      class="group relative m-0 inline-block rounded-sm align-top"
      :class="[widthClass, { 'ring-2 ring-primary-500 ring-offset-2': selected }]"
    >
      <img
        v-if="!broken"
        :src="node.attrs.src"
        :alt="t('messages.editor.imageAlt')"
        class="block w-full cursor-move rounded-sm"
        draggable="false"
        data-drag-handle
        contenteditable="false"
        @error="broken = true"
      />
      <div
        v-else
        contenteditable="false"
        class="flex h-32 w-full flex-col items-center justify-center gap-2 rounded-sm border border-dashed border-surface-300 bg-surface-50 text-surface-500 dark:border-surface-600 dark:bg-surface-800 dark:text-surface-400"
      >
        <span class="pi pi-image text-2xl"></span>
        <span class="text-xs">{{ t('messages.editor.imageMissing') }}</span>
      </div>

      <!-- z-10: while the image is still loading (zero height) the figure
           collapses and the caption, later in DOM order, would otherwise
           paint over these buttons and intercept their clicks. -->
      <div
        contenteditable="false"
        class="absolute right-1 top-1 z-10 flex items-center gap-0.5 rounded-sm bg-surface-900/75 p-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100"
        :class="{ 'opacity-100': selected }"
      >
        <button
          v-for="size in IMAGE_SIZES"
          :key="size"
          type="button"
          class="size-btn rte-tt"
          :class="{ 'is-active': node.attrs.size === size }"
          :aria-label="t(`messages.editor.imageSize_${size}`)"
          :data-tt="t(`messages.editor.imageSize_${size}`)"
          @click.stop="updateAttributes({ size })"
        >
          {{ size.toUpperCase() }}
        </button>
        <span class="mx-0.5 h-4 w-px bg-surface-500"></span>
        <button
          type="button"
          class="size-btn rte-tt"
          :aria-label="t('messages.editor.removeImage')"
          :data-tt="t('messages.editor.removeImage')"
          @click.stop="deleteNode()"
        >
          <span class="pi pi-times text-xs"></span>
        </button>
      </div>

      <!-- The caption IS the node's inline content: toolbar marks apply,
           Enter adds a line, and it exports as paragraphs under the image. -->
      <div class="relative">
        <NodeViewContent as="figcaption" class="caption-content" />
        <span v-if="captionEmpty" class="caption-placeholder" contenteditable="false">
          {{ t('messages.editor.imageCaptionPlaceholder') }}
        </span>
      </div>
    </figure>
  </NodeViewWrapper>
</template>

<script setup>
import { NodeViewContent, NodeViewWrapper, nodeViewProps } from '@tiptap/vue-3'
import { computed, shallowRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { IMAGE_SIZES } from '@/components/editor/extensions/planningImage'

const props = defineProps(nodeViewProps)

const { t } = useI18n()

const broken = shallowRef(false)

const captionEmpty = computed(() => props.node.content.size === 0)

// Widths echo the export presets (170pt / 343pt / 465pt of ~465pt text width).
const widthClass = computed(
  () =>
    ({
      s: 'w-[37%]',
      m: 'w-[74%]',
      l: 'w-full',
    })[props.node.attrs.size] ?? 'w-[74%]',
)
</script>

<style scoped>
  @reference '../../../css/tailwind.css';

  .size-btn {
    @apply flex h-6 min-w-6 cursor-pointer items-center justify-center rounded-sm border-none bg-transparent px-1 text-xs font-semibold text-white;

    &:hover {
      @apply bg-surface-700;
    }

    &.is-active {
      @apply bg-primary-500;
    }
  }

  .caption-content {
    @apply mt-1 block w-full text-sm leading-snug text-surface-700 outline-hidden dark:text-surface-300;

    /* Room for the cursor even when the caption is empty. */
    min-height: 1.25rem;
  }

  .caption-placeholder {
    @apply pointer-events-none absolute left-0 top-1 select-none text-sm italic leading-snug text-surface-400 dark:text-surface-500;
  }
</style>
